import { useEffect, useRef } from "react";
import {
  ReactFlow,
  type Node,
  type Edge,
  type NodeTypes,
  type ReactFlowInstance,
  Background,
  BackgroundVariant,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

/** Zoom limits. Shared by the <ReactFlow> props AND the pinch handler below,
 *  because two literals that have to agree are a bug waiting to be written. */
const MIN_ZOOM = 0.04;
const MAX_ZOOM = 2.5;

/** React Flow's own `panOnScrollSpeed` default. Copied, not chosen — a swipe
 *  over a pane has to feel identical to a swipe over bare canvas. */
const PAN_ON_SCROLL_SPEED = 0.5;

/** A trackpad gesture arrives as a burst of wheel events ~16ms apart. Anything
 *  quieter than this gap starts a NEW gesture, which is when we re-decide who
 *  gets it. See `wheelOwner` below for why the decision is latched at all. */
const GESTURE_GAP_MS = 150;

/**
 * Can anything between `from` and `wrap` actually scroll `axis` in direction
 * `sign` — right now, with room still left?
 *
 * This is the whole swipe rule, and the point is that NOTHING has to declare
 * itself: the DOM already knows what overflows and how far it has been
 * scrolled. A class (`nowheel`) is a component's guess about the future; this
 * is the truth at the moment of the gesture.
 */
function scrollsInDirection(
  from: EventTarget | null,
  wrap: HTMLElement,
  axis: "x" | "y",
  sign: number,
): boolean {
  let el = from instanceof Element ? from : null;
  while (el && el !== wrap) {
    if (el instanceof HTMLElement) {
      const cs = getComputedStyle(el);
      const overflow = axis === "x" ? cs.overflowX : cs.overflowY;
      if (overflow === "auto" || overflow === "scroll") {
        const max =
          axis === "x"
            ? el.scrollWidth - el.clientWidth
            : el.scrollHeight - el.clientHeight;
        // >1 not >0: sub-pixel layout rounding makes almost every box look
        // scrollable by a fraction of a pixel.
        if (max > 1) {
          const pos = axis === "x" ? el.scrollLeft : el.scrollTop;
          // RTL boxes run scrollLeft from -max to 0 (the Hebrew content on the
          // Falcon board is exactly why this is not theoretical).
          const rtl = axis === "x" && cs.direction === "rtl";
          const room =
            sign > 0
              ? rtl
                ? -pos
                : max - pos
              : rtl
                ? max + pos
                : pos;
          if (room > 1) return true;
        }
      }
    }
    el = el.parentElement;
  }
  return false;
}

/**
 * BoardCanvas — the ONE shared React Flow canvas every board renders through.
 *
 * Canonical trackpad-first interaction (matches the Marketing board, the
 * reference): two-finger swipe pans, pinch zooms, grab-hand everywhere, and
 * both work even OVER an embedded component. Do NOT hand-roll a <ReactFlow> in
 * a board file; use this, so interaction is identical on every board and can
 * never drift again.
 *
 * THE TWO GESTURE LAWS, both owned here and enforced for every board at once
 * (Erez, 2026-08-11) — the wheel handler below is the whole implementation:
 *   1. PINCH ALWAYS ZOOMS. No component can opt out.
 *   2. SWIPE ALWAYS PANS — unless whatever is under the cursor can genuinely
 *      scroll that way and still has room, in which case it scrolls and the
 *      board takes over again at the end. Read from the DOM, not from a class.
 *
 * DOUBLE-CLICK TO ZOOM (Erez, 2026-07-25): double-clicking any element marked
 * `data-zoom` frames THAT element edge-to-edge. `closest()` picks the nearest
 * marked ancestor, so the behaviour nests naturally — double-click a card and
 * you get the card, double-click the gray around it and you get the whole
 * section. Nothing marked under the cursor → React Flow's own step-zoom still
 * runs, so double-clicking empty canvas behaves as before.
 *
 * Mark an element by adding `data-zoom` to it. Already marked: SectionBox
 * content, DocumentFrame sheets, and board cards/diagram panels that opt in.
 *
 * (Extracted from MarketingBoardPage — Jarvis, 2026-07-19, board-standardization.)
 */
/**
 * ⛔ THE COLOUR OF EVERY BOARD, IN ONE PLACE.
 *
 * Erez, 23 Aug: "we need to make one variable that controls the colour of all
 * the boards ... I don't want you to go and do one by one ... and I want the
 * board to be white."
 *
 * Before this there was no such variable. This file held a default and EIGHT
 * board pages passed their own `background={T.bg}` over the top of it, each
 * from a private `T` constant in its own file — so Milo, Cleo, Remy and Leo
 * were #E5E5E5 while Quinn, Otto, Dave and the rest took the #F3F3F3 default.
 * That is precisely what he was looking at on the agents board: eleven tiles
 * that should read as one system, in two greys, for no reason a reader could
 * see. A default is not a variable when everyone is free to override it.
 *
 * The overrides are gone; these two are the only place a board colour is
 * decided. Change it here and every board on the dashboard moves together.
 *
 * WHITE ALSO HELPS THE THING THE OLD GREY WAS FOR. The comment this replaces
 * kept the canvas lighter than the console's #E5E5E5 so an embedded board tile
 * had a visible edge against the board it sits on. White is further from
 * #E5E5E5 than #F3F3F3 was, so that edge gets stronger, not weaker.
 */
export const BOARD_BG = "#FFFFFF";
/** The dot grid. Quiet enough to read as texture on white rather than as content. */
export const BOARD_DOTS = "#EDEDED";

export function BoardCanvas({
  nodes,
  nodeTypes,
  edges = [],
  background = BOARD_BG,
  dotColor = BOARD_DOTS,
  className,
  fitViewPadding = 0.12,
}: {
  nodes: Node[];
  nodeTypes: NodeTypes;
  edges?: Edge[];
  /** Canvas background behind the dots. Default board gray. */
  background?: string;
  /** Dot-grid color. */
  dotColor?: string;
  /** Optional wrapper class (e.g. a board-specific hook). */
  className?: string;
  fitViewPadding?: number;
}) {
  // Instance comes from onInit, not useReactFlow(): this component RENDERS
  // <ReactFlow>, so it sits outside that provider and the hook would throw.
  const rfiRef = useRef<ReactFlowInstance | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // A NATIVE capture listener on the wrapper — deliberately not React's
  // onDoubleClickCapture. React Flow zooms on double-click through d3-zoom,
  // which listens natively on the pane; React's synthetic handler lost that
  // race in the PRODUCTION build (worked in dev, gave d3's step-zoom on prod —
  // 2026-07-25). A native capture listener on an ANCESTOR always runs first,
  // and stopPropagation() here means d3 never sees the event at all.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onDbl = (e: MouseEvent) => {
      const rfi = rfiRef.current;
      if (!rfi) return;
      const target = e.target as HTMLElement | null;
      const el = target?.closest?.("[data-zoom]") as HTMLElement | null;
      // Nothing zoomable under the cursor → let React Flow's own zoom run.
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      const r = el.getBoundingClientRect();
      const tl = rfi.screenToFlowPosition({ x: r.left, y: r.top });
      const br = rfi.screenToFlowPosition({ x: r.right, y: r.bottom });
      rfi.fitBounds(
        { x: tl.x, y: tl.y, width: br.x - tl.x, height: br.y - tl.y },
        { duration: 500, padding: 0.05 },
      );
    };
    wrap.addEventListener("dblclick", onDbl, true);
    return () => wrap.removeEventListener("dblclick", onDbl, true);
  }, []);

  // ── ZOOM ALWAYS WINS (Erez, 2026-08-11) ────────────────────────────────────
  // Pinching over an embedded component did nothing at all. The cause is in
  // React Flow, and it is worth writing down because it is not obvious:
  //
  //   A TRACKPAD PINCH IS A `wheel` EVENT WITH ctrlKey SET. It is not a
  //   separate gesture the browser reports.
  //
  // So every wheel opt-out swallows it. `@xyflow/system` bails in two places:
  //   createZoomOnScrollHandler → `if (preventZoom || hasNoWheelClass) return null`
  //   the d3 filter             → `.nowheel` on wheel, and `.nopan` on wheel
  //                               whenever panOnScroll is on, which it is here.
  // It even calls preventDefault() first, so the BROWSER does not zoom either.
  // Between `.nowheel` (scrollable panes) and `.nopan` (labels, buttons), most
  // of a busy board was a dead zone.
  //
  // WHY THIS AND NOT A FIX PER COMPONENT: twelve files use those classes, and
  // they use them CORRECTLY — a scrollable pane must keep plain wheel, a label
  // must not start a pan. The classes are right; what was missing is that ZOOM
  // is a canvas-level concern and should never have been delegated to whatever
  // happens to sit under the cursor. So the canvas takes it back, in one place,
  // for every board at once. No component changes, and none can opt out again.
  //
  // Same mechanism as the double-click handler above and for the same reason:
  // a native CAPTURE listener on an ancestor runs before d3's own listener on
  // the pane, so stopping it there means React Flow never sees the event.
  //
  // The maths is d3-zoom's own cursor-anchored formula: keep the point under
  // the pointer fixed while the scale changes.
  //
  // ── AND SWIPE ALWAYS PANS, UNLESS THE THING UNDER IT REALLY SCROLLS ────────
  // (Erez, 2026-08-11 — the twin problem, and his rule, not mine.)
  //
  // Same cause as the zoom bug: a two-finger swipe is a plain `wheel` event, so
  // `.nowheel` / `.nopan` swallowed it and most of a busy board would not pan.
  //
  // My first proposal was to make embedded panes GROW instead of scroll, so the
  // canvas is the only scroller. Erez rejected it and he was right: that removes
  // scrolling to avoid the conflict. His rule RESOLVES it, and it is what the
  // browser already does natively for nested scrollers —
  //
  //   THE THING UNDER THE CURSOR WINS ONLY IF IT CAN ACTUALLY SCROLL IN THE
  //   DIRECTION YOU SWIPED, AND STILL HAS ROOM LEFT. OTHERWISE THE BOARD PANS.
  //
  // So: sideways over a KB page pans the board (that page has no X overflow);
  // down over it scrolls the page, and the moment it bottoms out the board takes
  // over; sideways over the kanban scrolls the columns; over a label, a card or
  // bare canvas, always the board. Nothing declares any of this — `nowheel` has
  // stopped being the decision, because the DOM knows better than a class does.
  //
  // TWO THINGS THAT ARE EASY TO GET WRONG:
  //  • When the pane wins we stopPropagation but NEVER preventDefault. Native
  //    scrolling is a default action, not a listener — preventing it here would
  //    leave the pane frozen and nothing would move at all.
  //  • The decision is LATCHED for the gesture. A diagonal swipe flips dominant
  //    axis between events, and re-deciding per event makes it stutter between
  //    scrolling the pane and panning the board. macOS latches its scroller the
  //    same way, so this also just matches the platform.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    // Who owns the wheel for the gesture in flight, and when we last saw one.
    const gesture = { last: -Infinity, letItScroll: false };
    const onWheel = (e: WheelEvent) => {
      const rfi = rfiRef.current;
      if (!rfi) return;

      // ── Plain wheel: swipe. Pan the board, or stand aside for a real scroller.
      if (!e.ctrlKey) {
        if (e.timeStamp - gesture.last >= GESTURE_GAP_MS) {
          // Dominant axis: a swipe is rarely purely one, and mixing them is what
          // makes the decision ambiguous in the first place.
          const axis = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? "x" : "y";
          const delta = axis === "x" ? e.deltaX : e.deltaY;
          gesture.letItScroll =
            delta !== 0 &&
            scrollsInDirection(e.target, wrap, axis, Math.sign(delta));
        }
        gesture.last = e.timeStamp;
        if (gesture.letItScroll) {
          // The pane's job. Hide it from React Flow, and let the browser scroll.
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        // @xyflow/system's createPanOnScrollHandler, in viewport terms:
        // translateBy(-(deltaX / k) * speed, ...) lands as x -= deltaX * speed.
        // The ×20 is their Firefox line-mode normalisation. Copied, not tuned.
        const n = e.deltaMode === 1 ? 20 : 1;
        const v = rfi.getViewport();
        rfi.setViewport({
          zoom: v.zoom,
          x: v.x - e.deltaX * n * PAN_ON_SCROLL_SPEED,
          y: v.y - e.deltaY * n * PAN_ON_SCROLL_SPEED,
        });
        return;
      }

      // ── ctrlKey: pinch. Zoom, always, wherever the cursor is.
      e.preventDefault();
      e.stopPropagation();

      const { x, y, zoom } = rfi.getViewport();
      const rect = wrap.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      // d3-zoom's OWN wheelDelta, copied rather than invented — it is the
      // formula @xyflow/system uses (dist/esm/index.js, `-event.deltaY * ...`)
      // and d3-zoom before it, including the ×10 that applies specifically to
      // ctrlKey. Using anything else makes our pinch feel different from the
      // native pinch on the parts of the board that never broke, and my first
      // attempt at a constant was 6× too aggressive — measured, not guessed.
      const delta =
        -e.deltaY *
        (e.deltaMode === 1 ? 0.05 : e.deltaMode ? 1 : 0.002) *
        10;
      const next = Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, zoom * Math.pow(2, delta)),
      );
      if (next === zoom) return;
      const k = next / zoom;
      rfi.setViewport({ zoom: next, x: px - (px - x) * k, y: py - (py - y) * k });
    };
    // passive:false — preventDefault on a wheel listener is ignored otherwise,
    // and without it the browser page-zooms on top of us.
    wrap.addEventListener("wheel", onWheel, { capture: true, passive: false });
    return () =>
      wrap.removeEventListener("wheel", onWheel, { capture: true } as EventListenerOptions);
  }, []);

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{ width: "100%", height: "100%", minHeight: 0, background }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onInit={(instance) => {
          rfiRef.current = instance;
        }}
        fitView
        fitViewOptions={{ padding: fitViewPadding, maxZoom: 1 }}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        selectionOnDrag={false}
        // panOnScroll is kept but is no longer what pans: the capture listener
        // above handles every wheel event before React Flow sees one. It stays
        // so that deleting that listener restores the OLD behaviour rather than
        // no behaviour at all.
        panOnScroll
        panOnDrag
        zoomOnPinch
        zoomOnDoubleClick
        onlyRenderVisibleElements
      >
        <Background variant={BackgroundVariant.Dots} gap={26} size={1.3} color={dotColor} />
      </ReactFlow>
    </div>
  );
}
